import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "../../../design/dialog";
import { EmploymentInfo } from "../../../models/EmploymentInfo";
import { useParticipant } from "../../../contexts/ParticipantContext";
import { ErrorMessage } from "../../../design/fieldset";
import { Button } from "../../../design/button";
import { Input } from "../../../design/input";
import { ApolloService } from "../../../services/ApolloService";
import { ApolloPerson } from "../../../models/ApolloPerson";
import { Avatar } from "../../../design/avatar"; // Optional: if you have one

interface Props {
  participantId: string;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  employmentInfo?: EmploymentInfo;
  onSaved?: () => void;
}

interface Errors {
  email?: string;
}

const EnrichParticipantDialog: React.FC<Props> = ({
  participantId,
  isOpen,
  setIsOpen,
  onSaved,
}) => {
  const { enrichParticipant } = useParticipant();
  const [isSaving, setIsSaving] = useState(false);
  const [email, setEmail] = useState("");
  const [person, setPerson] = useState<ApolloPerson | null>(null);
  const [errors, setErrors] = useState<Errors>({});

  const validate = () => {
    const newErrors: Errors = {};
    if (!email.trim()) {
      newErrors.email = "Email is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLookup = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      const match = await ApolloService.match(email);
      setPerson(match);
    } catch (err) {
      console.error("Match failed", err);
      setErrors({ email: "No match found or API error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!person) return;
    setIsSaving(true);
    try {
      await enrichParticipant(participantId, person.id);
      onSaved?.();
      setIsOpen(false);
      setPerson(null);
      setEmail("");
    } catch (err) {
      console.error("Enrichment failed", err);
      setErrors({ email: "Failed to enrich participant" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    setPerson(null);
    setEmail("");
    setErrors({});
  };

  return (
    <Dialog open={isOpen} onClose={handleCancel}>
      <DialogTitle>Enrich Participant</DialogTitle>
      <DialogDescription>
        {person
          ? "Confirm this match before enriching the participant."
          : "Enter an email to look up additional data."}
      </DialogDescription>
      <DialogBody>
        {!person ? (
          <>
            <Input
              placeholder="Enter email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
          </>
        ) : (
          <div className="flex items-center gap-4">
            {person.photo_url && (
              <img
                src={person.photo_url}
                alt={person.full_name}
                className="w-12 h-12 rounded-full"
              />
            )}
            <div>
              <div className="font-medium">{person.full_name}</div>
              <div className="text-sm text-gray-600">
                {person.title}{" "}
                {person.organization_name
                  ? `@ ${person.organization_name}`
                  : ""}
              </div>
              <div className="text-sm text-gray-500">{person.email}</div>
            </div>
          </div>
        )}
      </DialogBody>
      <DialogActions>
        <Button type="button" onClick={handleCancel} color="red">
          Cancel
        </Button>
        {!person ? (
          <Button
            type="button"
            onClick={handleLookup}
            disabled={isSaving}
            color="green"
          >
            {isSaving ? "Searching..." : "Search"}
          </Button>
        ) : (
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isSaving}
            color="green"
          >
            {isSaving ? "Saving..." : "Confirm"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EnrichParticipantDialog;
